import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { t } from 'i18next';

import reducer from './reducer';
import { questionTypes } from '../../../../../utils/enums';

const useQuestion = (question, appInstanceId, shouldShowErrors, handleQuestion) => {
    const [state, dispatch] = React.useReducer(reducer, { ...question, isSaving: false, appInstanceId, showErrors: shouldShowErrors });
    const prevVal = usePrevious(choosePrev(state));

    React.useEffect(() => {
        if (shouldShowErrors) dispatch({ type: 'showErrors' });
        else dispatch({ type: 'hideErrors' });
    }, [shouldShowErrors]);

    //#region PlainText Validator
    const questionValidatorPlainText = (question) => {
        let validityObj = { isValid: true, invalidReason: '' };

        if (question.minResponseRequired > 0 && question.answerText.length === 0) {
            validityObj = { isValid: false, invalidReason: 'Application.Required' };
            dispatch({ type: 'update', payload: validityObj });
            return validityObj;
        }

        const regexes = question.responseRegexes || [];
        if (regexes.length < 1) {
            validityObj = { isValid: true, invalidReason: '' };
            dispatch({ type: 'update', payload: validityObj });
            return validityObj;
        }

        for (let i = 0; i < regexes.length; i++) {
            const validator = regexes[i];
            try {
                var rgx = new RegExp(validator.rgx);
            } catch (_rgxE) {
                console.error(`Question ${question.id} has invalid regex in position ${i}.`);
                continue;
            }
            if (rgx.test(question.answerText)) continue;

            validityObj = { isValid: false, invalidReason: rgx.errorText };
            dispatch({ type: 'update', payload: validityObj });
            return validityObj;
        }

        dispatch({ type: 'update', payload: validityObj });
        return validityObj;
    };
    //#endregion PlainText Validator

    //#region MultiChoice Validator
    const questionValidatorMultiChoice = (question) => {
        let { chosenOptions, minResponseRequired, maxResponseRequired } = question;

        let validityObj = { isValid: true, invalidReason: '' };

        if (minResponseRequired === 0 || minResponseRequired === undefined) {
            validityObj = { isValid: true, invalidReason: '' };
            dispatch({ type: 'update', payload: validityObj });
            return validityObj;
        }

        if (minResponseRequired > 0 && chosenOptions.length < minResponseRequired) {
            validityObj = { isValid: false, invalidReason: `(At least ${minResponseRequired} options are required)` };
            dispatch({ type: 'update', payload: validityObj });
            return validityObj;
        }

        if (maxResponseRequired > 0 && chosenOptions.length > maxResponseRequired) {
            validityObj = { isValid: false, invalidReason: `(Please choose no more than ${maxResponseRequired} responses)` };
            dispatch({ type: 'update', payload: validityObj });
            return validityObj;
        }

        dispatch({ type: 'update', payload: validityObj });
        return validityObj;
    };
    //#endregion MultiChoice Validator

    const update = (newVal) => {
        dispatch({ type: 'hideErrors' });

        let newState = {},
            validityObj = {};

        switch (state.questionType) {
            case questionTypes.SingleLinePlainText:
            case questionTypes.MultiLinePlainText:
                validityObj = questionValidatorPlainText({ ...state, answerText: newVal });
                newState.answerText = newVal;
                break;
            case questionTypes.Checkbox:
            case questionTypes.DropdownMultichoice:
                validityObj = questionValidatorMultiChoice({ ...state, chosenOptions: newVal });
                newState.chosenOptions = newVal;
                break;
            default:
                console.log('Unknown question type');
        }

        dispatch({ type: 'update', payload: newState });
        handleQuestion({ ...state, ...newState, ...validityObj });
    };

    const save = async () => {
        let isDifferent,
            requestBody = {};
        switch (state.questionType) {
            case questionTypes.SingleLinePlainText:
            case questionTypes.MultiLinePlainText:
                isDifferent = prevVal !== state.answerText;
                requestBody.responseText = state.answerText;
                break;
            case questionTypes.Checkbox:
            case questionTypes.DropdownMultichoice:
                isDifferent = prevVal !== state.chosenOptions;
                requestBody.responseIds = state.chosenOptions;
                break;
            default:
                isDifferent = false;
        }

        if (state.isValid && isDifferent) {
            dispatch({ type: 'saving' });

            try {
                var updateAnswerResponse = await API.put('ShipyardAPI', `/application/${state.appInstanceId}/answer/${state.id}`, {
                    body: requestBody,
                });
            } catch (apiErr) {
                toast.error(t('General.AjaxError'));
            } finally {
                dispatch({ type: 'saved' });
            }

            if (!updateAnswerResponse.success) {
                toast.error(t('General.AjaxError'));
            }
        } else {
            dispatch({ type: 'showErrors' });
        }
    };

    return { ...state, update, save };
};

const choosePrev = (state) => {
    const { questionType, answerText, chosenOptions } = state;

    let value;
    switch (questionType) {
        case questionTypes.Checkbox:
            value = chosenOptions;
            break;
        default:
            value = answerText;
    }

    return value;
};

const usePrevious = (value) => {
    const ref = React.useRef(value);

    React.useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
};

export default useQuestion;
